import { useParams } from "react-router-dom";
import { fetchCustomerInfo } from "../../api/orders.api";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";

const CustomerVerificationPage = () => {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["sp-qr-order-info", orderId],

    queryFn: () => fetchCustomerInfo(orderId as string),

    enabled: !!orderId,

    retry: false,

    staleTime: 1000 * 60 * 5,
  });

  // LOADING
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-5" />

          <p className="text-xl font-bold text-slate-700">
            Loading Customer Details...
          </p>
        </div>
      </div>
    );
  }

  // ERROR
  if (isError || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-4xl font-black mx-auto mb-6">
            ✕
          </div>

          <h1 className="text-3xl font-black text-slate-800 mb-3">
            Invalid Invoice
          </h1>

          <p className="text-slate-500">
            {(error as Error)?.message || "Invoice not found"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-cyan-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-[32px] shadow-2xl overflow-hidden border border-white/60">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 p-8 md:p-10 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div>
              <p className="uppercase tracking-[0.3em] text-emerald-100 text-xs font-semibold mb-3">
                National Healthcare & Wellness Mission
              </p>

              <h1 className="text-4xl md:text-5xl font-black tracking-tight">
                Verified Customer
              </h1>

              <p className="mt-4 text-emerald-50 max-w-2xl leading-relaxed">
                This invoice and customer information has been successfully
                verified through the official QR verification system.
              </p>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="p-5 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT */}
          <div className="lg:col-span-2 space-y-6">
            {/* CUSTOMER */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black text-slate-800">
                    Customer Details
                  </h2>

                  <p className="text-slate-500 mt-1">
                    Verified shipping information
                  </p>
                </div>

                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 text-white flex items-center justify-center text-2xl font-black shadow-lg">
                  {order.address.name[0]}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">
                    Customer Name
                  </p>

                  <p className="text-lg font-bold text-slate-800">
                    {order.address.name}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">
                    Phone Number
                  </p>

                  <p className="text-lg font-bold text-slate-800">
                    {order.address.phone}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 md:col-span-2">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">
                    Shipping Address
                  </p>

                  <p className="text-base leading-relaxed font-semibold text-slate-700">
                    {order.address.addressLine}, {order.address.city},{" "}
                    {order.address.state} {order.address.postalCode}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            {/* TOTAL */}
            <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-4 shadow-2xl overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-500/20" />

              <div className="relative z-10">
                <p className="uppercase tracking-[0.25em] text-xs text-emerald-300 mb-3">
                  Total Amount Paid
                </p>

                <h2 className="text-5xl font-black tracking-tight">
                  ₹{order.total_amount}
                </h2>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm">
                  <div className="flex justify-between mb-4">
                    <span className="text-slate-300">Order Number: </span>

                    <span className="font-bold">{order.order_number}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-slate-300">Order Date: </span>

                    <span className="text-slate-300">
                      {new Date(order?.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* VERIFIED CARD */}
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="min-h-full rounded-2xl bg-emerald-500 text-white flex items-center justify-center font-black shadow-lg">
                  <ShieldCheck size={40} />
                </div>

                <div>
                  <h3 className="text-lg font-black text-emerald-800 mb-1">
                    Authentic Invoice
                  </h3>

                  <p className="text-emerald-700 leading-normal text-sm">
                    This invoice has been digitally verified through the
                    official QR invoice verification system.
                  </p>
                </div>
              </div>
            </div>

            {/* SUPPORT */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-2xl font-black text-slate-800 mb-5">
                Need Help?
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="uppercase tracking-widest text-xs text-slate-400 mb-2">
                    Support Number
                  </p>

                  <p className="text-lg font-bold text-slate-800">
                    +91 9707076232
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="uppercase tracking-widest text-xs text-slate-400 mb-2">
                    Email Support
                  </p>

                  <p className="font-semibold text-slate-700 break-all">
                    nhwm.info@gmail.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerVerificationPage;
